import type { DocumentAction } from "./permissions";

/** Asset document kinds = API route segments (`/api/assets/companies/{id}/<kind>`). */
export const documentKinds = [
  "acquisitions",
  "settlements",
  "transfers",
  "sales",
  "scrappings",
  "impairments",
  "revaluations",
] as const;
export type DocumentKind = (typeof documentKinds)[number];

/** List route of each kind. */
export const listPaths = {
  acquisitions: "/assets/acquisitions",
  settlements: "/assets/settlements",
  transfers: "/assets/transfers",
  sales: "/assets/sales",
  scrappings: "/assets/scrappings",
  impairments: "/assets/impairments",
  revaluations: "/assets/revaluations",
} as const satisfies Record<DocumentKind, string>;

/** Object route of each kind. */
export const objectPaths = {
  acquisitions: "/assets/acquisitions/$documentId",
  settlements: "/assets/settlements/$documentId",
  transfers: "/assets/transfers/$documentId",
  sales: "/assets/sales/$documentId",
  scrappings: "/assets/scrappings/$documentId",
  impairments: "/assets/impairments/$documentId",
  revaluations: "/assets/revaluations/$documentId",
} as const satisfies Record<DocumentKind, string>;

/** Legacy permission feature of each kind (FA19: the legacy screens keep their keys). */
const features: Record<DocumentKind, string> = {
  acquisitions: "asset_purchase",
  settlements: "auc_settlement",
  transfers: "assets_movement",
  sales: "asset_sell",
  scrappings: "asset_stock_out_damage",
  impairments: "impairment",
  revaluations: "revaluation",
};

export const kindPermission = (kind: DocumentKind, action: DocumentAction): string =>
  `assets.${features[kind]}.${action}`;

/** Which line fields a kind uses (the API validates the same, contract §4). */
export interface KindShape {
  amount: "none" | "required" | "optional";
  offsetAccount: boolean;
  fraction: boolean;
  destination: boolean;
  customer: boolean;
  takeover: boolean;
  sourceAsset: boolean;
}

export const kindShape: Record<DocumentKind, KindShape> = {
  acquisitions: {
    amount: "required",
    offsetAccount: true,
    fraction: false,
    destination: false,
    customer: false,
    takeover: true,
    sourceAsset: false,
  },
  settlements: {
    amount: "optional",
    offsetAccount: false,
    fraction: false,
    destination: false,
    customer: false,
    takeover: false,
    sourceAsset: true,
  },
  transfers: {
    amount: "none",
    offsetAccount: false,
    fraction: false,
    destination: true,
    customer: false,
    takeover: false,
    sourceAsset: false,
  },
  sales: {
    amount: "required",
    offsetAccount: true,
    fraction: true,
    destination: false,
    customer: true,
    takeover: false,
    sourceAsset: false,
  },
  scrappings: {
    amount: "none",
    offsetAccount: false,
    fraction: true,
    destination: false,
    customer: false,
    takeover: false,
    sourceAsset: false,
  },
  impairments: {
    amount: "required",
    offsetAccount: false,
    fraction: false,
    destination: false,
    customer: false,
    takeover: false,
    sourceAsset: false,
  },
  revaluations: {
    amount: "required",
    offsetAccount: false,
    fraction: false,
    destination: false,
    customer: false,
    takeover: false,
    sourceAsset: false,
  },
};

/** Report routes (`/assets/reports/<key>`) and the permission of each. */
export const reportKeys = ["register", "roll-forward", "depreciation-forecast", "lease-maturity"] as const;
export type ReportKey = (typeof reportKeys)[number];
